import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, Plus, Trash2 } from 'lucide-react';
import {
  CASH_SOURCES,
  formatIqd,
  fromMillidinars,
  SUGGESTED_EXPENSE_CATEGORIES,
  SUGGESTED_INCOME_CATEGORIES,
  type CashSource,
  type DRAWER_REFUSALS,
  type FINANCE_REFUSALS,
  type FinanceEntryKind,
} from '@gc/shared';

import { FilterDisclosure } from '@/components/layout/FilterDisclosure';
import { useAuth } from '@/features/auth/useAuth';
import { ApiError } from '@/lib/api';
import {
  cashSourceFor,
  createEntry,
  deleteEntry,
  useDrawerInUse,
  useFinance,
  type FinanceFilters,
} from './useFinance';

/** The server's refusal of a Drawer entry while the cash drawer is not in use. */
const DRAWER_NOT_ENABLED: (typeof DRAWER_REFUSALS)[number] = 'drawer_not_enabled';

/** The server's refusal to change or delete an entry whose drawer session was closed. */
const FINANCE_ENTRY_FROZEN: (typeof FINANCE_REFUSALS)[number] = 'finance_entry_frozen';

/**
 * Expenses and Incomes (SPEC §5.7).
 *
 * These two tables are the only source for the Overall Report's `Expenses` and
 * manual `Incomes` lines — everything else on that report is derived from
 * bills. Until this screen existed there was no way to enter either, so the
 * report showed no costs at all and overstated Net Profit by the venue's whole
 * cost base.
 *
 * Each new entry also says where its money came from or went (owner,
 * 2026-10-12): the cash drawer, the safe, or outside (the bank, the owner's
 * pocket). Only a Drawer entry changes what the drawer should hold and only a
 * Safe one writes a safe slip; all three count toward Net Profit alike. The
 * list shows that source for every entry ("Not recorded" for the rows from
 * before the drawer, never guessed), and an entry whose drawer session was
 * counted and closed carries a lock: its figure sits in a stored close, so it
 * can no longer be deleted — a correction is a new entry.
 */
export function FinancePage() {
  const { t } = useTranslation();
  const { venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';

  const [kind, setKind] = useState<FinanceEntryKind>('EXPENSE');
  const [filters, setFilters] = useState<FinanceFilters>({ from: '', to: '', search: '' });
  const [adding, setAdding] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data, loading, error, reload } = useFinance(kind, filters);

  const categories = useMemo(
    () => (kind === 'EXPENSE' ? SUGGESTED_EXPENSE_CATEGORIES : SUGGESTED_INCOME_CATEGORIES),
    [kind],
  );

  const controlClass =
    'rounded-control border border-subtle bg-input px-2.5 py-1.5 text-sm text-content outline-none focus:border-primary';

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 p-4">
        <h1 className="text-xl font-semibold text-content">{t('finance.title')}</h1>

        <div className="flex overflow-hidden rounded-control border border-subtle bg-raised">
          {(['EXPENSE', 'INCOME'] as const).map((entry) => (
            <button
              key={entry}
              type="button"
              onClick={() => {
                setKind(entry);
                setDeleteError(null);
              }}
              aria-pressed={kind === entry}
              className={[
                'px-4 py-1.5 text-sm transition-colors',
                kind === entry
                  ? 'bg-primary font-medium text-primary-fg'
                  : 'text-content-secondary hover:bg-hover',
              ].join(' ')}
            >
              {t(entry === 'EXPENSE' ? 'finance.expenses' : 'finance.incomes')}
            </button>
          ))}
        </div>

        {/* Phone: the date range folds behind Filters, so the ledger is not
            four rows of controls further down. Desktop: the same row. */}
        <FilterDisclosure
          storageKey="finance.filters"
          badge={(filters.from ? 1 : 0) + (filters.to ? 1 : 0)}
        >
          <label className="flex items-center gap-1.5 text-xs text-content-secondary">
            {t('common.from')}
            <input
              type="date"
              value={filters.from}
              onChange={(event) => setFilters((f) => ({ ...f, from: event.target.value }))}
              className={`${controlClass} max-md:flex-1`}
            />
          </label>
          <label className="flex items-center gap-1.5 text-xs text-content-secondary">
            {t('common.to')}
            <input
              type="date"
              value={filters.to}
              onChange={(event) => setFilters((f) => ({ ...f, to: event.target.value }))}
              className={`${controlClass} max-md:flex-1`}
            />
          </label>
        </FilterDisclosure>

        <input
          value={filters.search}
          onChange={(event) => setFilters((f) => ({ ...f, search: event.target.value }))}
          placeholder={t('finance.searchPlaceholder')}
          className={`${controlClass} w-56`}
        />

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => setAdding(true)}
          className="flex items-center gap-1.5 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg hover:bg-primary-hover"
        >
          <Plus className="h-4 w-4" aria-hidden />
          {t(kind === 'EXPENSE' ? 'finance.addExpense' : 'finance.addIncome')}
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-auto px-4">
        {deleteError ? (
          <p role="alert" data-delete-error className="mb-3 text-sm text-danger">
            {deleteError}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : loading && !data ? (
          <p className="text-sm text-content-muted">{t('common.loading')}</p>
        ) : !data || data.entries.length === 0 ? (
          <p className="text-sm text-content-muted">{t('common.noResults')}</p>
        ) : (
          <table className="w-full border-collapse text-sm">
            <thead className="sticky top-0 bg-overlay">
              <tr className="border-b border-strong text-start text-[10px] uppercase tracking-[0.14em] text-content-muted">
                <Th>{t('finance.businessDate')}</Th>
                <Th>{t('finance.description')}</Th>
                <Th>{t('finance.category')}</Th>
                <Th>{t(kind === 'EXPENSE' ? 'finance.paidFrom' : 'finance.receivedInto')}</Th>
                <Th>{t('bills.columns.receiptEditor')}</Th>
                <Th align="end">{t('common.total')}</Th>
                <Th align="end">{t('common.actions')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((entry, index) => (
                <tr
                  key={entry.id}
                  data-entry-id={entry.id}
                  className={index % 2 === 0 ? 'bg-row-odd' : 'bg-row-even'}
                >
                  <Td className="tabular">{entry.businessDate}</Td>
                  <Td>{entry.description}</Td>
                  <Td className="text-content-secondary">{entry.category ?? '—'}</Td>
                  <Td className="text-content-secondary">
                    {entry.cashSource
                      ? t(`finance.cashSources.${entry.cashSource}`)
                      : t('finance.cashSourceUnknown')}
                    {entry.frozen ? (
                      <span
                        data-frozen-badge
                        title={t('finance.entryFrozen')}
                        className="ms-2 inline-flex items-center gap-1 rounded-full border border-subtle px-1.5 py-0.5 text-[10px] text-content-muted"
                      >
                        <Lock className="h-3 w-3" aria-hidden />
                        {t('finance.frozenBadge')}
                      </span>
                    ) : null}
                  </Td>
                  <Td className="text-content-secondary">{entry.employeeName ?? '—'}</Td>
                  <Td align="end" className="tabular font-medium">
                    {formatIqd(fromMillidinars(entry.amountMd), { currencySymbol })}
                  </Td>
                  <Td align="end">
                    {/* A locked entry sits in a stored drawer close: the server
                        refuses it (finance_entry_frozen), so the button says so first. */}
                    <button
                      type="button"
                      onClick={() => void handleDelete(entry.kind, entry.id)}
                      disabled={entry.frozen === true}
                      aria-label={t('common.delete')}
                      title={entry.frozen ? t('finance.entryFrozen') : t('common.delete')}
                      className="rounded-control p-1 text-content-muted hover:bg-hover hover:text-danger disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-content-muted"
                    >
                      <Trash2 className="h-4 w-4" aria-hidden />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Footer totals the FILTERED set, matching how Bills does it. */}
      <footer className="flex items-center justify-end gap-6 border-t border-subtle p-4 text-sm">
        <span className="text-content-secondary">
          {t('common.itemCount')} <span className="tabular text-content">{data?.count ?? 0}</span>
        </span>
        <span className="text-content-secondary">
          {t('common.total')}{' '}
          {/* Money leaving the venue reads in danger, not accent — spending
              is not a figure to celebrate with a glow. */}
          <span className="tabular text-lg font-bold text-danger">
            {formatIqd(fromMillidinars(data?.totalMd ?? '0'), { currencySymbol })}
          </span>
        </span>
      </footer>

      {adding ? (
        <EntryDialog
          kind={kind}
          categories={categories}
          onClose={() => setAdding(false)}
          onCreated={() => void reload()}
        />
      ) : null}
    </div>
  );

  /** Routed by the ROW's kind, never the tab's: the two can disagree mid-switch. */
  async function handleDelete(entryKind: FinanceEntryKind, id: string): Promise<void> {
    // A manual entry references nothing and nothing references it, so unlike a
    // bill it is genuinely deletable — a mistyped rent figure is best removed
    // rather than left as a voided ghost in the report.
    if (!window.confirm(t('finance.confirmDelete'))) return;
    setDeleteError(null);
    try {
      await deleteEntry(entryKind, id);
    } catch (caught) {
      // Said out loud: a refused or lost delete used to vanish as an unhandled
      // rejection, the row still there and nothing telling anyone why.
      setDeleteError(
        caught instanceof ApiError && caught.code === FINANCE_ENTRY_FROZEN
          ? t('finance.entryFrozen')
          : caught instanceof ApiError
            ? caught.message
            : t('finance.deleteFailed'),
      );
    }
    // Either way the list is re-read — a 404 means somebody else already
    // removed it, and the table should stop offering it.
    void reload();
  }
}

function EntryDialog({
  kind,
  categories,
  onClose,
  onCreated,
}: {
  kind: FinanceEntryKind;
  categories: readonly string[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const { employee, can } = useAuth();
  // Read when the dialog opens, not with the page: the list's own reads
  // stay exactly as they were.
  const drawerInUse = useDrawerInUse();
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [amountIqd, setAmountIqd] = useState('');
  const [businessDate, setBusinessDate] = useState('');
  const [note, setNote] = useState('');
  /** Null until someone taps a source: the default follows the drawer and who is typing. */
  const [pickedSource, setPickedSource] = useState<CashSource | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const source = cashSourceFor(pickedSource, drawerInUse, employee?.isOwner === true);

  const fieldClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-sm text-content outline-none focus:border-primary';

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await createEntry(kind, {
        description,
        amountIqd: Number(amountIqd) || 0,
        ...(category ? { category } : {}),
        ...(businessDate ? { businessDate } : {}),
        ...(note ? { note } : {}),
        // Always sent: a new entry never joins the "unknown" rows from before
        // the drawer. The server refuses Drawer while the drawer is off.
        ...(kind === 'EXPENSE' ? { paidFrom: source } : { receivedInto: source }),
      });
      onCreated();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError && caught.code === DRAWER_NOT_ENABLED
          ? t('finance.drawerNotEnabled')
          : caught instanceof ApiError
            ? caught.message
            : t('finance.saveFailed'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="w-full max-w-md rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold text-content">
          {t(kind === 'EXPENSE' ? 'finance.addExpense' : 'finance.addIncome')}
        </h2>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('finance.description')}
          </span>
          <input
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            required
            autoFocus
            className={fieldClass}
          />
        </label>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('finance.category')}
            </span>
            {/* Free text with suggestions, not a fixed list: a venue will have
                costs nobody anticipated, and a migration is a poor answer to
                "we started paying for a water delivery". */}
            <input
              list="finance-categories"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              className={fieldClass}
            />
            <datalist id="finance-categories">
              {categories.map((entry) => (
                <option key={entry} value={entry} />
              ))}
            </datalist>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">{t('finance.amount')}</span>
            <input
              type="number"
              min="1"
              step="1"
              value={amountIqd}
              onChange={(event) => setAmountIqd(event.target.value)}
              required
              className={`${fieldClass} tabular`}
            />
          </label>
        </div>

        <label className="mb-3 block">
          <span className="mb-1 block text-sm text-content-secondary">
            {t('finance.businessDate')}
          </span>
          <input
            type="date"
            value={businessDate}
            onChange={(event) => setBusinessDate(event.target.value)}
            className={`${fieldClass} tabular`}
          />
          <span className="mt-1 block text-xs text-content-muted">
            {t('finance.businessDateHint')}
          </span>
        </label>

        {/* Where the money came from or went (owner, 2026-10-12). Buttons,
            not a select: the choice decides what the drawer should hold, so
            it is read at a glance rather than opened. */}
        <div className="mb-3">
          <span className="mb-1 block text-sm text-content-secondary">
            {t(kind === 'EXPENSE' ? 'finance.paidFrom' : 'finance.receivedInto')}
          </span>
          <div className="flex overflow-hidden rounded-control border border-subtle bg-raised">
            {CASH_SOURCES.map((entry) => (
              <button
                key={entry}
                type="button"
                data-cash-source={entry}
                onClick={() => setPickedSource(entry)}
                // Refused by the server while the drawer is off, so said here
                // first. Unknown (a failed read) leaves it open. The Safe is
                // for those who may move safe money: the server answers
                // anyone else with a bare 403.
                disabled={
                  (entry === 'DRAWER' && drawerInUse === false) ||
                  (entry === 'SAFE' && !can('safe.manage'))
                }
                aria-pressed={source === entry}
                className={[
                  'flex-1 px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40',
                  source === entry
                    ? 'bg-primary font-medium text-primary-fg'
                    : 'text-content-secondary hover:bg-hover',
                ].join(' ')}
              >
                {t(`finance.cashSources.${entry}`)}
              </button>
            ))}
          </div>
          <span className="mt-1 block text-xs text-content-muted">
            {drawerInUse === false ? `${t('finance.drawerOff')} ` : ''}
            {t(`finance.cashSourceHints.${source}`)}
          </span>
        </div>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm text-content-secondary">{t('finance.note')}</span>
          <textarea
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={2}
            className={fieldClass}
          />
        </label>

        {error ? (
          <p role="alert" className="mb-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );
}

function Th({ children, align }: { children: React.ReactNode; align?: 'end' }) {
  return (
    <th className={`px-3 py-2 font-medium ${align === 'end' ? 'text-end' : 'text-start'}`}>
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  className = '',
}: {
  children: React.ReactNode;
  align?: 'end';
  className?: string;
}) {
  return (
    <td className={`px-3 py-2 ${align === 'end' ? 'text-end' : 'text-start'} ${className}`}>
      {children}
    </td>
  );
}
